<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useRouter, useRoute } from 'vue-router'
import useInactivityMonitor from '@/composables/useInactivityMonitor'
import InactivityWarningModal from '@/components/modals/InactivityWarningModal.vue'
import { useNotificationsStore } from '@/stores/notifications'

const urlTmktProd = import.meta.env.TMKT_LEGADO_URL

const drawer = ref(true)
const rail = ref(false)
const authStore = useAuthStore()
const router = useRouter()
const notificationsStore = useNotificationsStore()

// Função para obter a URL completa da imagem do usuário
const getUserImageUrl = (imagePath) => {
  if (!imagePath) return null
  return urlTmktProd + `/images/users-pic/${imagePath}`
}

// Sistema de monitoramento de inatividade
const {
  isWarningVisible,
  remainingSeconds,
  continueSession,
  performLogout,
  startMonitoring,
  stopMonitoring,
  isMonitoringPaused,
} = useInactivityMonitor()

const route = useRoute()

// Iniciar monitoramento quando usuário estiver autenticado e parar quando não estiver
watch(
  () => authStore.isAuthenticated,
  (isAuth) => {
    if (isAuth) {
      // Pequeno atraso para garantir que tudo esteja inicializado
      setTimeout(() => {
        startMonitoring()
        authStore.startPolling()
      }, 100)
    } else {
      stopMonitoring()
      authStore.stopPolling()
    }
  },
  { immediate: true },
)

// Use computed properties to access notification data from the store
const newMessagesCount = computed(() => notificationsStore.newMessagesCount)
const newRatingsCount = computed(() => notificationsStore.newRatingsCount)
const newMarketpriceCount = computed(() => notificationsStore.newMarketpriceCount)
const newAssistenciasCount = computed(() => notificationsStore.newAssistenciasCount)
const rankingUsers = computed(() => notificationsStore.rankingUsers)

// Buscar notificações quando o componente for montado e configurar o timer
onMounted(() => {
  if (authStore.isAuthenticated) {
    notificationsStore.startNotificationPolling()
  }
})

// Limpar o timer quando o componente for desmontado
onUnmounted(() => {
  notificationsStore.stopNotificationPolling()
  stopMonitoring()
  authStore.stopPolling()
})

// Atualizar a estrutura do menu para incluir subitens
const allMenuItems = [
  {
    title: 'Administração',
    icon: 'mdi-shield-account',
    children: [
      { title: 'Usuários', icon: 'mdi-account-group', to: '/usuarios', nome_fisico: 'usuarios' },
      {
        title: 'Usuários Online',
        icon: 'mdi-account-check',
        to: '/usuarios-online',
        nome_fisico: 'usuarios',
      },
      { title: 'Perfis de Acesso', icon: 'mdi-account-key', to: '/perfis', nome_fisico: 'perfis' },
      {
        title: 'Mudança Mapa TMKT',
        icon: 'mdi-map-outline',
        to: '/historico-mapas-revendas',
        nome_fisico: 'mudanca-no-mapa-log',
      },
      {
        title: 'Mensagens Débito',
        icon: 'mdi-message-alert',
        to: '/mensagens',
        nome_fisico: 'mensagens-debito',
      },
      { title: 'Configurações', icon: 'mdi-cog', to: '/variaveis', nome_fisico: 'config' },
      {
        title: 'Alíquota do Imposto',
        icon: 'mdi-cash-edit',
        to: '/aliquota-imposto',
        nome_fisico: 'aliquota-imposto',
      },
      {
        title: 'Preços Produtos',
        icon: 'mdi-tag-multiple',
        to: '/precos-produtos',
        nome_fisico: 'precos-produtos',
      },
    ],
  },
  {
    title: 'Boletos',
    icon: 'mdi-file-document-outline',
    children: [
      {
        title: 'Emissão de Boletos',
        icon: 'mdi-file-send-outline',
        to: '/boleto-emitir',
        nome_fisico: 'boleto-emitir',
      },
      {
        title: 'Gerenciar Boletos',
        icon: 'mdi-file-cog-outline',
        to: '/boleto-gerenciar',
        nome_fisico: 'boleto-gerenciar',
      },
    ],
  },
  {
    title: 'Envios Whatsapp',
    icon: 'mdi-whatsapp',
    children: [
      {
        title: 'Boleto Pós',
        icon: 'mdi-file-send',
        to: '/boleto-enviar-whatsapp',
        nome_fisico: 'boleto-enviar-whatsapp',
      },
      {
        title: 'Boleto Pré',
        icon: 'mdi-cash-refund',
        to: '/boleto-pre-enviar-whatsapp',
        nome_fisico: 'enviar-whatsapp-prepago',
      },
    ],
  },
  {
    title: 'Envio Marketing',
    icon: 'mdi-bell',
    children: [
      {
        title: 'Enviar Campanha',
        icon: 'mdi-send',
        to: '/campanha-marketing',
        nome_fisico: 'enviar-campanha-marketing',
      },
      {
        title: 'Campanhas SMS',
        icon: 'mdi-history',
        to: '/sms-marketing-lista',
        nome_fisico: 'listar-sms-cliente',
      },
    ],
  },
  {
    title: 'Pedidos',
    icon: 'mdi-clipboard-list',
    children: [
      {
        title: 'Gerenciar Pedidos',
        icon: 'mdi-cart',
        to: '/gerenciar-pedidos',
        nome_fisico: 'gerenciar-pedidos',
      },
      {
        title: 'Realizar Pedido',
        icon: 'mdi-propane-tank',
        to: '/realizar-pedido',
        nome_fisico: 'realizar-pedido-endereco_externa_desativados',
      },
      {
        title: 'Realizar Pedido [TESTAR]',
        icon: 'mdi-propane-tank',
        to: '/realizar-pedido-tomtom',
        nome_fisico: 'realizar-pedido-tomtom',
      },
      {
        title: 'Gerenciar Assistências',
        icon: 'mdi-tools',
        to: '/assistencia-tecnica',
        nome_fisico: 'assistencia-tecnica',
      },
      {
        title: 'Pedidos Pendentes Histórico',
        icon: 'mdi-clock-alert',
        to: '/pendente-historico',
        nome_fisico: 'pendente-historico',
      }
    ],
  },
  {
    title: 'Cadastro',
    icon: 'mdi-store',
    children: [
      { title: 'Revendas', icon: 'mdi-domain', to: '/revendas', nome_fisico: 'gestao-revendas' },
      {
        title: 'Revendas no Mapa',
        icon: 'mdi-map-marker',
        to: '/revendas-mapa',
        nome_fisico: 'revendas-mapa',
      },
      {
        title: 'Avaliações Play Store',
        icon: 'mdi-google-play',
        to: '/avaliacao-play-store',
        nome_fisico: 'avaliacao-play-store',
      },
    ],
  },

  {
    title: 'Clientes',
    icon: 'mdi-account-multiple',
    children: [
      {
        title: 'Mensagens Cliente',
        icon: 'mdi-message-text',
        to: '/mensagens-cliente',
        nome_fisico: 'mensagens-cliente',
      },
      {
        title: 'Controle de Bot',
        icon: 'mdi-robot',
        to: '/bot-controle',
        nome_fisico: 'bot-controle',
      },
      { title: 'Avaliações', icon: 'mdi-star', to: '/avaliacao', nome_fisico: 'avaliacao' },
      {
        title: 'Pós Venda Avaliações',
        icon: 'mdi-google',
        to: '/pos-venda-avaliacao-google',
        nome_fisico: 'pos-venda-avaliacao-google',
      },
    ],
  },
  {
    title: 'Gestão de Revendas',
    icon: 'mdi-chart-bar',
    children: [
      {
        title: 'Revendas na Vitrine',
        icon: 'mdi-chart-timeline-variant-shimmer',
        to: '/revendas/metricas',
        nome_fisico: 'revendas-na-vitrine',
      },
      {
        title: 'Pedidos Entregues',
        icon: 'mdi-truck-check',
        to: '/pedidos-entregues',
        nome_fisico: 'pedidos-entregues',
      },
    ],
  },
]

// Estado para controlar qual grupo de menu está expandido
const expandedGroup = ref(null)

// Adicionar watcher para fechar os submenus quando entrar em modo rail
watch(
  () => rail.value,
  (isRail) => {
    if (isRail) {
      // Quando entrar em modo rail, colapsa qualquer grupo expandido
      expandedGroup.value = null
    }
  },
)

// Função para alternar a expansão de um grupo
const toggleGroup = (event, groupTitle) => {
  // Prevent default group expansion behavior
  event.stopPropagation()

  // Se o menu estiver em modo rail (compacto), expandir o menu lateral
  if (rail.value) {
    rail.value = false
  }

  if (expandedGroup.value === groupTitle) {
    expandedGroup.value = null
  } else {
    expandedGroup.value = groupTitle
  }
}

// Calculando itens de menu a serem exibidos com base nas permissões
const menuItems = computed(() => {
  if (!authStore.user || !authStore.user.paginas) {
    return []
  }

  const allowedPageNames = authStore.user.paginas.map((pagina) => pagina.nome_fisico)

  // Filtrar e processar grupos de menu e seus filhos
  return allMenuItems
    .map((group) => {
      // Filtrar apenas os filhos que o usuário tem permissão para ver
      const filteredChildren = group.children.filter((child) =>
        allowedPageNames.includes(child.nome_fisico),
      )

      // Retornar o grupo com os filhos filtrados
      return {
        ...group,
        children: filteredChildren,
        // Somente mostrar grupos que têm pelo menos um filho com permissão
        visible: filteredChildren.length > 0,
      }
    })
    .filter((group) => group.visible)
})

const userMenuItems = [
  { title: 'Perfil', icon: 'mdi-account', to: '/meus-dados' },
  { title: 'Sair', icon: 'mdi-logout', action: 'logout' },
]

const handleUserMenuAction = async (action) => {
  if (action === 'logout') {
    await authStore.logout()
    router.push('/login')
  }
}

// Function to check if a group has a child with assistências badge
const hasAssistenciasBadge = (group) => {
  if (!newAssistenciasCount.value) return false
  return group.children.some((child) => child.title === 'Gerenciar Assistências')
}
</script>

<template>
  <v-app>
    <!-- App Bar with custom height -->
    <v-app-bar color="primary" class="custom-app-bar">
      <v-app-bar-nav-icon @click="drawer = !drawer" color="white"></v-app-bar-nav-icon>
      <v-app-bar-title class="white--text">Preço do Gás - TMKT</v-app-bar-title>
      <v-spacer></v-spacer>

      <!-- Ranking de Vendas -->
      <div v-if="rankingUsers.length > 0" class="d-none d-md-flex align-center me-4 ranking-container">
        <v-tooltip location="bottom">
          <template v-slot:activator="{ props }">
            <div class="ranking-title me-2" v-bind="props">
              <v-icon color="amber-darken-1" class="me-1" size="40">mdi-seal</v-icon>
            </div>
          </template>
          <span>Ranking dos atendentes com mais pedidos</span>
        </v-tooltip>

        <div v-for="(user, index) in rankingUsers.slice(0, 3)" :key="user.idatendente" class="ranking-item">
          <v-tooltip location="bottom" :text="`${user.nome}: ${user.total} pedidos`">
            <template v-slot:activator="{ props }">
              <div class="ranking-user-container" v-bind="props">
                <v-avatar size="40" :class="`ranking-avatar rank-${index + 1}`">
                  <v-img v-if="user.foto" :src="getUserImageUrl(user.foto)" alt="foto usuário"></v-img>
                  <v-icon v-else size="28">mdi-account</v-icon>
                </v-avatar>

                <div class="ranking-user-info">
                  <div class="ranking-user-name">{{ user.nome.split(' ')[0] }}</div>
                  <div class="ranking-user-count">{{ user.total }}</div>
                </div>
              </div>
            </template>
          </v-tooltip>
        </div>
      </div>

      <!-- Alertas -->

      <!-- Notificação de alterações no marketprice -->
      <v-btn icon class="me-2" color="white" title="Preços alterados" target="_blank"
        :href="`${urlTmktProd}/marketprice_minor_prices`">
        <v-badge :content="newMarketpriceCount" title="Preços alterados no marketprice" color="info"
          :model-value="newMarketpriceCount > 0">
          <v-icon class="icones-menu-superior">mdi-tag-multiple</v-icon>
        </v-badge>
      </v-btn>

      <!-- Notificação de assistências técnicas em aberto -->
      <v-btn icon class="me-2" color="white" title="Assistências técnicas em aberto" :to="'/assistencia-tecnica'">
        <v-badge :content="newAssistenciasCount" title="Assistências técnicas em aberto" color="error"
          :model-value="newAssistenciasCount > 0">
          <v-icon class="icones-menu-superior">mdi-tools</v-icon>
        </v-badge>
      </v-btn>

      <v-btn icon class="me-2" color="white" title="Novas avaliações" :href="'/avaliacao'">
        <v-badge :content="newRatingsCount" title="Novas avaliações" color="warning" :model-value="newRatingsCount > 0">
          <v-icon class="icones-menu-superior">mdi-room-service</v-icon>
        </v-badge>
      </v-btn>

      <v-btn icon class="me-2" color="white" title="Mensagens em aberto" :to="'/mensagens-cliente'">
        <v-badge :content="newMessagesCount" title="Mensagens em aberto" color="error"
          :model-value="newMessagesCount > 0">
          <v-icon class="icones-menu-superior">mdi-bell</v-icon>
        </v-badge>
      </v-btn>

      <!-- Avatar e menu do usuário -->
      <v-menu location="bottom end">
        <template v-slot:activator="{ props }">
          <v-btn icon v-bind="props" :class="authStore.user?.foto ? '' : 'user-icon-default'"
            :color="authStore.user?.foto ? 'white' : 'secondary'">
            <v-avatar size="40">
              <v-img v-if="authStore.user?.foto" :src="getUserImageUrl(authStore.user?.foto)"
                :alt="authStore.user?.nome"></v-img>
              <template v-else>
                <v-icon size="32">mdi-account</v-icon>
              </template>
            </v-avatar>
          </v-btn>
        </template>
        <v-list>
          <v-list-item v-for="(item, index) in userMenuItems" :key="index" :to="item.action ? undefined : item.to"
            @click="item.action ? handleUserMenuAction(item.action) : undefined">
            <v-list-item-title>
              <v-icon start :icon="item.icon"></v-icon>
              {{ item.title }}
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>

    <!-- Navigation Drawer with nested menu -->
    <v-navigation-drawer v-model="drawer" :rail="rail" @click="rail = false" permanent width="270">
      <div class="user-section" :class="{ 'rail-mode': rail }">
        <div v-if="!rail" class="user-info">
          <div class="user-name">{{ authStore.user?.nome }}</div>
          <div class="user-role">{{ authStore.user?.perfil.descricao }}</div>
        </div>
        <v-btn variant="text" icon="mdi-chevron-left" @click.stop="rail = !rail" class="toggle-btn"></v-btn>
      </div>

      <v-divider></v-divider>

      <v-list nav>
        <!-- Menu com grupos e sub-itens -->
        <template v-for="(group, i) in menuItems" :key="`group-${i}`">
          <!-- Using manual expansion control instead of v-list-group's built-in behavior -->
          <div class="menu-group">
            <!-- Group header (parent item) -->
            <v-list-item @click="(e) => toggleGroup(e, group.title)" :prepend-icon="group.icon" :title="group.title"
              :class="{ 'group-item': !rail, active: expandedGroup === group.title }">
              <!-- Show badge on parent when in rail mode and group contains "Gerenciar Assistências" -->
              <template v-if="rail && hasAssistenciasBadge(group)" v-slot:append>
                <v-badge :content="newAssistenciasCount" color="error" :model-value="newAssistenciasCount > 0"
                  class="parent-badge"></v-badge>
              </template>

              <!-- Show chevron when not in rail mode -->
              <template v-else-if="!rail" v-slot:append>
                <v-icon :icon="expandedGroup === group.title ? 'mdi-chevron-up' : 'mdi-chevron-down'"></v-icon>
              </template>
            </v-list-item>

            <!-- Group children (conditionally rendered based on expanded state) -->
            <div v-show="expandedGroup === group.title" class="group-children">
              <v-list-item v-for="(child, j) in group.children" :key="`child-${i}-${j}`" :to="child.to"
                :prepend-icon="child.icon" :title="child.title" class="ml-4 sub-item" density="compact">
                <!-- Add badge for Gerenciar Assistências menu item when not in rail mode -->
                <template v-if="
                  !rail && child.title === 'Gerenciar Assistências' && newAssistenciasCount > 0
                " v-slot:append>
                  <v-badge :content="newAssistenciasCount" color="error" :model-value="newAssistenciasCount > 0"
                    class="sidebar-badge"></v-badge>
                </template>
              </v-list-item>
            </div>
          </div>
        </template>
      </v-list>
    </v-navigation-drawer>

    <!-- Main Content Area -->
    <v-main>
      <v-container class="pb-0" fluid>
        <RouterView />
      </v-container>
    </v-main>

    <!-- Modal de aviso de inatividade-->
    <InactivityWarningModal :open="isWarningVisible && !isMonitoringPaused" @update:open="isWarningVisible = $event"
      :remaining-seconds="remainingSeconds" @stay-active="continueSession" @logout="performLogout" />
  </v-app>
</template>

<style scoped>
.rail-user-item {
  min-height: 50px !important;
  height: 50px !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.user-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 16px;
  min-height: 64px;
  transition: all 0.3s ease;
}

.user-info {
  flex-grow: 1;
  overflow: hidden;
}

.user-name {
  font-weight: 500;
  font-size: 0.9375rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-role {
  font-size: 0.75rem;
  opacity: 0.7;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.toggle-btn {
  margin-left: 8px;
}

.rail-mode {
  padding: 8px 8px;
  min-height: 48px;
  justify-content: center;
}

/* Estilos para o ranking de vendas */
.ranking-container {
  display: flex;
  align-items: center;
}

.ranking-title {
  display: flex;
  align-items: center;
  color: white;
  font-weight: 500;
}

.ranking-item {
  margin-right: 12px;
  position: relative;
}

.ranking-user-container {
  display: flex;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.15);
  padding: 4px;
  border-radius: 24px;
}

.ranking-avatar {
  border: 2px solid white;
  margin-right: 8px;
}

.rank-1 {
  border-color: gold;
}

.rank-2 {
  border-color: silver;
}

.rank-3 {
  border-color: #cd7f32;
}

.ranking-user-info {
  margin-right: 10px;
  color: white;
  line-height: 1.1;
}

.ranking-user-name {
  font-size: 0.9rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 80px;
}

.ranking-user-count {
  font-size: 0.9rem;
  font-weight: bold;
  opacity: 0.9;
}

.custom-app-bar {
  height: 64px !important;
}

.icones-menu-superior {
  font-size: 30px;
}

/* Estilo para o botão de avatar do usuário quando não tem foto */
.user-icon-default {
  background-color: transparent !important;
  transition: all 0.3s ease;
}

.user-icon-default:hover {
  opacity: 0.9;
  box-shadow: 0 0 8px rgba(255, 255, 255, 0.5);
}

/* Styles for custom menu groups */
.menu-group {
  position: relative;
}

.group-item {
  font-weight: 600 !important;
  cursor: pointer;
}

.group-item.active {
  background-color: rgba(var(--v-theme-primary), 0.1);
  color: rgb(var(--v-theme-primary));
}

.group-children {
  padding-bottom: 8px;
  overflow: hidden;
}

.sub-item {
  font-size: 0.9rem;
  padding-left: 16px !important;
}

/* Add styles for the sidebar badge */
.sidebar-badge {
  margin-right: 8px;
}

/* Style for badge in rail mode on parent items */
.parent-badge {
  position: absolute;
  top: 8px;
  right: 8px;
}

/* Remove old v-list-group styles */
</style>
